import moment from "moment";
import type { Moment } from "moment";

import { DEFAULT_LOCALE, SAVE_DATE_FORMAT } from "@/v1/constants";

/*
 * Shared plumbing for the FsE forms. Written for the KapG form (SMT-1294); the
 * Einzelunternehmer form still carries its own copies and can adopt these in a
 * follow-up, which is a change to that form and therefore not part of this one.
 */

/**
 * Drops what ELSTER treats as "not answered": empty strings, nulls and empty
 * objects. antd hands back `null` for a cleared InputNumber and `""` for a
 * cleared Input, and both must reach the API as an absent key rather than as a
 * value the payload builder has to interpret.
 */
export const compactValue = (value: unknown): unknown => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (Array.isArray(value)) {
    return value.map(compactValue).filter((item) => item !== undefined);
  }
  if (value && typeof value === "object") {
    return compactObject(value as Record<string, unknown>);
  }
  return value === null ? undefined : value;
};

export const compactObject = <T extends object>(
  value: T
): Partial<T> | undefined => {
  const entries = Object.entries(value)
    .map(([key, child]) => [key, compactValue(child)] as const)
    .filter(([, child]) => child !== undefined);

  return entries.length
    ? (Object.fromEntries(entries) as Partial<T>)
    : undefined;
};

export const getDatePickerValue = (value?: string): Moment | null => {
  if (!value) return null;
  const date = moment(value.slice(0, 10), SAVE_DATE_FORMAT, true);
  return date.isValid() ? date : null;
};

export const getDatePickerFormValue = (
  value?: Moment | null
): string | undefined =>
  value?.isValid() ? value.format(SAVE_DATE_FORMAT) : undefined;

/**
 * ELSTER stores a street and a house number in separate fields, while the
 * questionnaire collects them in one input. The split is the last digit group
 * of the string ("Hauptstraße 12a" → "Hauptstraße" + "12a"); an address without
 * one keeps the whole string as the street, which is what a Postfach or a
 * foreign address looks like.
 *
 * Shared by both preview Datenarten (SMT-1335): the Einzelunternehmer list and
 * the KapG Zeilen 4, 8, 28 and 74 all show the two halves apart.
 */
export const splitStreetForPreview = (
  street?: string
): { street?: string; houseNumber?: string } => {
  if (!street) {
    return {};
  }

  const normalizedStreet = street.replace(/\s+/g, " ").trim();
  const [, streetName, houseNumber] =
    normalizedStreet.match(/^(.+?)\s+([0-9][0-9A-Za-z-]*)$/) ?? [];

  return {
    street: streetName ?? normalizedStreet,
    houseNumber,
  };
};

/**
 * A file size an operator can read, in the locale the Backoffice runs in.
 * Kilobytes below a megabyte and one decimal above it: the exact byte count of
 * a PDF says nothing, while "9,8 MB" against a 10 MB limit says everything
 * (SMT-1341).
 */
export const formatFileSize = (size: number): string => {
  const kilobytes = size / 1024;
  if (kilobytes < 1024) {
    return `${new Intl.NumberFormat(DEFAULT_LOCALE, {
      maximumFractionDigits: 0,
    }).format(Math.max(1, kilobytes))} KB`;
  }

  return `${new Intl.NumberFormat(DEFAULT_LOCALE, {
    maximumFractionDigits: 1,
  }).format(kilobytes / 1024)} MB`;
};
